import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function PlanLimitModal({ isOpen, onClose, variant = 'consumer' }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  if (!isOpen) return null;
  
  const isExecutor = variant === 'executor' || user?.role === 'executor';
  
  const heading = isExecutor 
    ? 'Plan limit reached' 
    : 'You have reached your plan limit';
  
  const body = isExecutor
    ? 'Upgrade to access full estate management features including unlimited notifications and document storage.'
    : 'Your current Starter plan includes a limited number of vault entries. Upgrade to Guardian to store unlimited accounts, documents and assets - everything your executor will need.';
  
  const handleUpgrade = () => {
    onClose();
    navigate('/settings?tab=subscription');
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="plan-limit-modal">
      <div className="bg-[var(--safekept-card)] rounded-2xl p-6 w-full max-w-md border border-[var(--safekept-border)] shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <h3 className="font-bold text-lg text-[var(--safekept-text)]">{heading}</h3>
        </div>
        
        <p className="text-sm text-[var(--safekept-text-secondary)] mb-6 leading-relaxed">
          {body}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            onClick={handleUpgrade}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white min-h-[44px] rounded-xl font-semibold"
            data-testid="upgrade-plan-btn"
          >
            Upgrade to {isExecutor ? 'Estate Pro' : 'Guardian'} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button 
            variant="outline" 
            onClick={onClose}
            className="flex-1 min-h-[44px] rounded-xl"
            data-testid="maybe-later-btn"
          >
            Maybe later
          </Button>
        </div>
      </div>
    </div>
  );
}

// Context for managing 402 modal state globally
import { createContext, useContext, useState, useCallback } from 'react';

const PlanLimitContext = createContext(null);

export function PlanLimitProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [variant, setVariant] = useState('consumer');
  
  const show402Modal = useCallback((v = 'consumer') => {
    setVariant(v);
    setIsOpen(true);
  }, []);
  
  const close402Modal = useCallback(() => {
    setIsOpen(false);
  }, []);
  
  return (
    <PlanLimitContext.Provider value={{ show402Modal, close402Modal }}>
      {children}
      <PlanLimitModal isOpen={isOpen} onClose={close402Modal} variant={variant} />
    </PlanLimitContext.Provider>
  );
}

export const usePlanLimit = () => {
  const ctx = useContext(PlanLimitContext);
  if (!ctx) throw new Error('usePlanLimit must be used within PlanLimitProvider');
  return ctx;
};
